import React from 'react';
import { SortAsc, SortDesc, ArrowUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Split student-name columns for the school portal tables.
 *
 * Coordinators asked for first and last name as SEPARATE columns so a
 * roster can be sorted by either one directly (2026-09-25 feedback from the
 * Anoka-Hennepin magnet coordinators). Every school table used to render one
 * combined "Student" cell and hid the first/last choice behind a dropdown.
 *
 * This module holds the pieces every table shares: the sort keys, the
 * migration of a legacy persisted sort key, and the two header cells.
 * Body cells stay in each table because their markup (avatar, ID line,
 * badges) differs per surface.
 */

export const FIRST_NAME_KEY = 'student_first_name' as const;
export const LAST_NAME_KEY = 'student_last_name' as const;
export type NameSortKey = typeof FIRST_NAME_KEY | typeof LAST_NAME_KEY;

/** The combined-name key older builds persisted in localStorage. */
const LEGACY_COMBINED_KEY = 'student_name';

/**
 * Map a persisted sort key from before the split onto the last-name column,
 * which is what the combined column sorted by. Any other key passes through.
 */
export function migrateLegacyNameSortKey<T extends string>(key: T | typeof LEGACY_COMBINED_KEY | null | undefined): T | typeof LAST_NAME_KEY | null {
  if (key == null) return null;
  if (key === LEGACY_COMBINED_KEY) return LAST_NAME_KEY;
  return key;
}

export interface SortState {
  key: string;
  direction: 'asc' | 'desc';
}

/**
 * Read a persisted `{key, direction}` sort config, migrating a legacy
 * combined-name key. Returns null for missing or unreadable values.
 */
export function readStoredSort(raw: string | null | undefined): SortState | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<SortState> | null;
    if (!parsed || typeof parsed.key !== 'string') return null;
    const key = migrateLegacyNameSortKey(parsed.key);
    if (!key) return null;
    return { key, direction: parsed.direction === 'desc' ? 'desc' : 'asc' };
  } catch {
    return null;
  }
}

/** Default roster order once the columns are split: last name A→Z. */
export const DEFAULT_NAME_SORT: SortState = { key: LAST_NAME_KEY, direction: 'asc' };

interface NameColumnHeadersProps {
  /** Primary sort currently applied to the table. */
  sort: SortState | null;
  /** Secondary sort (shift-click), if the table supports one. */
  secondarySort?: SortState | null;
  /** Called with the column key; the event lets tables keep shift-click semantics. */
  onSort: (key: NameSortKey, event: React.MouseEvent) => void;
  /** Class applied to each <th>; tables pass their own header styling. */
  thClassName?: string;
  /** Optional icon-size class for the sort glyph. */
  iconClassName?: string;
  firstLabel?: string;
  lastLabel?: string;
}

function SortGlyph({ field, sort, secondarySort, iconClassName }: { field: string; sort: SortState | null; secondarySort?: SortState | null; iconClassName?: string }) {
  const size = iconClassName ?? 'h-3 w-3';
  if (sort?.key === field) {
    return (
      <span className="inline-flex items-center gap-0.5" data-sort-role="primary">
        {sort.direction === 'asc' ? <SortAsc className={size} /> : <SortDesc className={size} />}
        {secondarySort && <span className="text-[9px] font-bold">1</span>}
      </span>
    );
  }
  if (secondarySort?.key === field) {
    return (
      <span className="inline-flex items-center gap-0.5" data-sort-role="secondary">
        {secondarySort.direction === 'asc' ? <SortAsc className={cn(size, 'text-gray-400')} /> : <SortDesc className={cn(size, 'text-gray-400')} />}
        <span className="text-[9px] font-bold text-gray-400">2</span>
      </span>
    );
  }
  return <ArrowUpDown className={cn(size, 'text-gray-300')} />;
}

/**
 * Two sortable header cells: "First Name" and "Last Name".
 * Render inside the table's header <tr> in place of the old combined cell.
 */
export function NameColumnHeaders({ sort, secondarySort = null, onSort, thClassName, iconClassName, firstLabel = 'First Name', lastLabel = 'Last Name' }: NameColumnHeadersProps) {
  const ariaSort = (field: string): React.AriaAttributes['aria-sort'] =>
    sort?.key === field ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none';
  const cols: Array<[NameSortKey, string]> = [[FIRST_NAME_KEY, firstLabel], [LAST_NAME_KEY, lastLabel]];
  return (
    <>
      {cols.map(([key, label]) => (
        <th
          key={key}
          scope="col"
          aria-sort={ariaSort(key)}
          data-sort-key={key}
          className={cn('cursor-pointer select-none', thClassName)}
          title="Click to sort, Shift+Click for secondary sort"
          onClick={(e) => onSort(key, e)}
        >
          <div className="flex items-center gap-1">
            {label} <SortGlyph field={key} sort={sort} secondarySort={secondarySort} iconClassName={iconClassName} />
          </div>
        </th>
      ))}
    </>
  );
}

/**
 * Case-insensitive comparator for a name part; blanks sort last in both
 * directions so an unnamed row never floats to the top.
 */
export function compareNamePart(a: string | null | undefined, b: string | null | undefined, direction: 'asc' | 'desc'): number {
  const av = (a ?? '').trim().toLowerCase();
  const bv = (b ?? '').trim().toLowerCase();
  if (!av && !bv) return 0;
  if (!av) return 1;
  if (!bv) return -1;
  const cmp = av.localeCompare(bv);
  return direction === 'asc' ? cmp : -cmp;
}
